import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";

const NAV_LINKS = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/commercial", label: "Commercial" },
    { to: "/residential", label: "Luxury Residential" },
    { to: "/services", label: "Services" },
    { to: "/agents", label: "Agents" },
    { to: "/events", label: "Events" },
    { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location]);

    return (
        <header
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "backdrop-blur-2xl bg-black/70 border-b border-brand-gold/20"
                    : "backdrop-blur-sm bg-black/30 border-b border-transparent"
            }`}
            data-testid="site-navbar"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="nav-home-link">
                    <span className="font-cursive text-3xl sm:text-4xl text-brand-gold leading-none whitespace-nowrap">
                        Real Estate
                    </span>
                    <span className="hidden 2xl:inline-block text-[0.58rem] tracking-[0.4em] text-brand-silver/70 uppercase border-l border-brand-gold/30 pl-3 whitespace-nowrap">
                        &amp; Administration Inc
                    </span>
                </Link>

                <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            className={({ isActive }) =>
                                `relative text-[0.68rem] xl:text-[0.72rem] tracking-[0.25em] xl:tracking-[0.3em] uppercase whitespace-nowrap transition-colors duration-300 ${
                                    isActive
                                        ? "text-brand-gold"
                                        : "text-brand-silver/80 hover:text-brand-gold"
                                }`
                            }
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <Link
                    to="/contact"
                    className="hidden lg:inline-flex text-[0.7rem] tracking-[0.3em] uppercase border border-brand-gold/60 text-brand-gold px-5 py-3 hover:bg-brand-gold hover:text-brand-jet transition-all duration-500"
                    data-testid="nav-cta-consult"
                >
                    Private Consultation
                </Link>

                <button
                    className="lg:hidden text-brand-gold p-2"
                    aria-label="Menu"
                    onClick={() => setOpen((o) => !o)}
                    data-testid="nav-mobile-toggle"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden bg-black/95 border-t border-brand-gold/20 px-6 py-8" data-testid="nav-mobile-panel">
                    <nav className="flex flex-col gap-5">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                className={({ isActive }) =>
                                    `text-sm tracking-[0.3em] uppercase ${
                                        isActive ? "text-brand-gold" : "text-brand-silver/80"
                                    }`
                                }
                                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            className="mt-4 text-xs tracking-[0.3em] uppercase border border-brand-gold/60 text-brand-gold px-5 py-4 text-center hover:bg-brand-gold hover:text-brand-jet transition-all"
                            data-testid="nav-mobile-cta"
                        >
                            Private Consultation
                        </Link>
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Navbar;
